import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import CampanaNotificaciones from "./CampanaNotificaciones";
import TablasCelular from "./TablasCelular";
import { useAyuda } from "../context/AyudaContext";
import { useParametros } from "../utils/parametros";
import { useApariencia, variablesDeRuta, variablesMenu } from "../utils/temas";
import { seccionesParaRoles } from "../utils/menu";

const ETIQUETAS_ROL = {
  solicitante: "Solicitante",
  aprobador: "Aprobador",
  compras: "Compras",
  administrador: "Administrador",
  pagos: "Pagos",
};

const CLAVE_MENU_PLEGADO = "menuPlegado";

const leerMenuPlegado = () => {
  try {
    return localStorage.getItem(CLAVE_MENU_PLEGADO) === "1";
  } catch {
    return false;
  }
};

const iniciales = (nombre = "") =>
  nombre
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join("");

function Layout({ usuario, onLogout }) {
  const esAprobador = usuario.rol === "aprobador" || usuario.rol === "administrador";
  const esCompras = usuario.rol === "compras" || usuario.rol === "administrador";
  const esAdmin = usuario.rol === "administrador";
  const esPagos = usuario.rol === "pagos";

  const { pathname } = useLocation();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [plegado, setPlegado] = useState(leerMenuPlegado);
  const { mostrarAyuda, alternarAyuda } = useAyuda();
  // Carga los parámetros del sistema (IVA, plazo del crédito…) al entrar.
  useParametros();
  const apariencia = useApariencia();

  const alternarPlegado = () => {
    setPlegado((actual) => {
      try {
        localStorage.setItem(CLAVE_MENU_PLEGADO, actual ? "0" : "1");
      } catch {
        // Sin almacenamiento: el menú vuelve abierto la próxima vez.
      }
      return !actual;
    });
  };

  // El menú (y la lista de pantallas de Apariencia) se define en utils/menu.js.
  const secciones = seccionesParaRoles({ esPagos, esCompras, esAprobador, esAdmin });

  const fechaHoy = new Date().toLocaleDateString("es-NI", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div style={{ ...styles.contenedor, ...variablesMenu(apariencia) }}>
      <div
        className={`app-sidebar-overlay${menuAbierto ? " visible" : ""}`}
        onClick={() => setMenuAbierto(false)}
      />

      <aside className={`app-sidebar${menuAbierto ? " abierta" : ""}${plegado ? " plegada" : ""}`} style={styles.sidebar}>
        <div className="menu-marca" style={styles.marca}>
          <div style={styles.marcaIcono}>🛒</div>
          <div className="menu-texto" style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.marcaTitulo}>Gestión de Compras</div>
            <div style={styles.marcaSubtitulo}>Colegio Alemán — DSM</div>
          </div>
          <button
            type="button"
            className="menu-plegar"
            style={styles.botonPlegar}
            onClick={alternarPlegado}
            aria-label={plegado ? "Mostrar el menú completo" : "Dejar solo los íconos"}
            title={plegado ? "Mostrar el menú completo" : "Dejar solo los íconos"}
          >
            {plegado ? "»" : "«"}
          </button>
        </div>

        <nav style={styles.nav} onClick={() => setMenuAbierto(false)}>
          {secciones.map((seccion) => {
            const enlaces = seccion.enlaces;
            return (
              <div key={seccion.titulo} style={styles.seccion}>
                <div className="menu-seccion" style={styles.seccionTitulo}>{seccion.titulo}</div>
                {enlaces.map((enlace) => (
                  <NavLink
                    key={enlace.ruta}
                    to={enlace.ruta}
                    end={enlace.exacta}
                    title={plegado ? enlace.texto : undefined}
                    className="menu-enlace"
                    style={({ isActive }) => ({
                      ...styles.link,
                      ...(isActive || (enlace.tambien && pathname.startsWith(enlace.tambien)) ? styles.linkActivo : {}),
                    })}
                  >
                    <span style={styles.icono}>{enlace.icono}</span>
                    <span className="menu-texto">{enlace.texto}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="menu-usuario" style={styles.usuario}>
          <div style={styles.avatar} title={usuario.nombre}>{iniciales(usuario.nombre)}</div>
          <div className="menu-texto" style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.usuarioNombre}>{usuario.nombre}</div>
            <div style={styles.usuarioRol}>{ETIQUETAS_ROL[usuario.rol] || usuario.rol}</div>
          </div>
          <button type="button" style={styles.botonSalir} onClick={onLogout} title="Cerrar sesión" aria-label="Cerrar sesión">
            ⏻
          </button>
        </div>
      </aside>

      <div style={styles.columnaDerecha}>
        <header className="app-topbar" style={styles.topbar}>
          <button
            type="button"
            className="mobile-menu-btn"
            style={styles.botonMenu}
            onClick={() => setMenuAbierto((v) => !v)}
            aria-label="Abrir menú"
          >
            ☰
          </button>
          <div className="topbar-fecha" style={styles.fecha}>{fechaHoy}</div>
          <button
            type="button"
            onClick={alternarAyuda}
            style={styles.botonAyuda}
            title={mostrarAyuda ? "Ocultar los textos de ayuda de las pantallas" : "Mostrar los textos de ayuda de las pantallas"}
          >
            {mostrarAyuda ? "🙈 Ocultar ayuda" : "💡 Mostrar ayuda"}
          </button>
          <CampanaNotificaciones />
        </header>

        <main className="app-main" style={{ ...styles.main, ...variablesDeRuta(pathname, apariencia) }}>
          <Outlet context={{ usuario }} />
        </main>
        <TablasCelular />
      </div>
    </div>
  );
}

const styles = {
  contenedor: { display: "flex", minHeight: "100vh", background: "var(--tema-titulo, #0b3c60)" },
  sidebar: {
    flexShrink: 0,
    background: "linear-gradient(180deg, var(--tema-titulo, #0b3c60) 55%, var(--tema-primario, #1f4e96) 160%)",
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: 0,
    height: "100vh",
    overflowY: "auto",
  },
  marca: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    padding: "1.1rem 0.9rem",
    borderBottom: "1px solid rgba(255,255,255,0.12)",
  },
  marcaIcono: {
    fontSize: "1.3rem",
    width: 36,
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    background: "var(--tema-acento, #fec900)",
    flexShrink: 0,
  },
  marcaTitulo: { fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.25 },
  marcaSubtitulo: { fontSize: "0.7rem", color: "#c7d0e0" },
  botonPlegar: {
    background: "rgba(255,255,255,0.1)",
    border: "none",
    color: "#fff",
    borderRadius: 7,
    width: 26,
    height: 26,
    cursor: "pointer",
    fontSize: "1rem",
    lineHeight: 1,
    flexShrink: 0,
  },
  nav: { display: "flex", flexDirection: "column", padding: "0.5rem 0.6rem", gap: "0.35rem", flex: 1 },
  seccion: { display: "flex", flexDirection: "column", gap: "0.15rem" },
  seccionTitulo: {
    fontSize: "0.66rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#c7d0e0",
    padding: "0.6rem 0.55rem 0.25rem",
  },
  link: {
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    color: "#e6ebf5",
    textDecoration: "none",
    fontSize: "0.86rem",
    padding: "0.36rem 0.5rem",
    borderRadius: 10,
  },
  linkActivo: {
    background: "#ffffff",
    color: "var(--tema-titulo, #0b3c60)",
    fontWeight: 700,
    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
  },
  // Cuadrito claro detrás del emoji: los íconos oscuros no se pierden sobre el fondo del menú.
  icono: {
    fontSize: "0.92rem",
    width: 28,
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    background: "rgba(255,255,255,0.88)",
    flexShrink: 0,
  },
  usuario: {
    display: "flex",
    alignItems: "center",
    gap: "0.55rem",
    padding: "0.75rem 0.8rem",
    borderTop: "1px solid rgba(255,255,255,0.12)",
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--tema-acento, #fec900)",
    color: "#1c2733",
    fontWeight: 800,
    fontSize: "0.76rem",
    flexShrink: 0,
  },
  usuarioNombre: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  usuarioRol: { fontSize: "0.68rem", color: "#c7d0e0" },
  botonSalir: {
    background: "rgba(255,255,255,0.1)",
    border: "none",
    color: "#fff",
    borderRadius: 8,
    width: 30,
    height: 30,
    cursor: "pointer",
    fontSize: "0.95rem",
    flexShrink: 0,
  },
  // Fondo de la aplicación: gris azulado muy suave para que las tarjetas blancas se distingan.
  columnaDerecha: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    background: "#f2f5f9",
  },
  topbar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "0.9rem",
    padding: "0.6rem 1.5rem",
    borderBottom: "1px solid #e2e6ea",
    background: "#fff",
    flexWrap: "wrap",
  },
  fecha: { flex: 1, fontSize: "0.8rem", color: "#5b6472" },
  botonMenu: {
    background: "transparent",
    border: "1px solid #d3d9df",
    color: "var(--tema-titulo, #0b3c60)",
    borderRadius: 6,
    padding: "0.35rem 0.6rem",
    cursor: "pointer",
    fontSize: "1.1rem",
    lineHeight: 1,
    alignItems: "center",
  },
  botonAyuda: {
    background: "transparent",
    border: "1px solid #d3d9df",
    color: "#5b6472",
    borderRadius: 999,
    padding: "0.35rem 0.75rem",
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  main: {
    flex: 1,
    padding: "1.5rem 2rem",
    maxWidth: 1600,
    width: "100%",
    margin: "0 auto",
    boxSizing: "border-box",
  },
};

export default Layout;
